import React, { useEffect, useRef, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';

import { usePromoLoyaltyPromoKeyOptions } from '~/hooks/usePromoLoyaltyPromoKeyOptions';

import { LoyaltyPointsBonusConfigV1, PromoLoyaltyMetaConfig, getProductTypeLabel } from '~/services/PromoService';

import Spinner from '../Common/Spinner';
import Accordion from '../v2/common/Accordion/Accordion';

import LoyaltyPromoKeyOptionsSelector from './LoyaltyPromoKeyOptionsSelector';
import LoyaltyPromoProductTypeOptionsSelector from './LoyaltyPromoProductTypeOptionsSelector';

type Props = {
  isPromoMetaLoading: boolean;
  config: LoyaltyPointsBonusConfigV1 | null;
  promoLoyaltyMetaConfig: PromoLoyaltyMetaConfig | null;
  loyaltyBonusApplyMethod: PromoLoyaltyMetaConfig['loyaltyBonusFilterMethods'][number]['key'] | null;
  onConfigChange: (config: LoyaltyPointsBonusConfigV1) => void;
  onChange?: (config: LoyaltyPointsBonusConfigV1) => void;
};

type FormData = {
  loyaltyPromoKey: string;
  loyaltyProductType: string;
  applyOrder: number;
};

type EditingItem = {
  id: string;
  loyaltyPromoKey: string;
  loyaltyProductType: string;
  applyOrder: number;
};

const LoyaltyPointsBonusConfig = ({
  isPromoMetaLoading,
  config,
  promoLoyaltyMetaConfig,
  loyaltyBonusApplyMethod,
  onConfigChange,
  onChange,
}: Props) => {
  // Generate unique UUID for each row
  const generateUniqueId = () => uuidv4();

  // Initialize local state from config
  const [localConfig, setLocalConfig] = useState(config?.loyaltyPointsBonusConfigV1 || []);
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const {
    data: loyaltyPromoKeyOptions,
    isLoading: isLoyaltyPromoKeyOptionsLoading,
    error: loyaltyPromoKeyOptionsError,
  } = usePromoLoyaltyPromoKeyOptions();

  // Use ref to track previous config to prevent infinite recursion
  const prevConfigRef = useRef(config);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      loyaltyPromoKey: '',
      loyaltyProductType: '',
      applyOrder: 0,
    },
  });

  // Sync local state when config prop changes
  useEffect(() => {
    // Only update if config has actually changed
    if (config !== prevConfigRef.current) {
      prevConfigRef.current = config;

      if (config?.loyaltyPointsBonusConfigV1) {
        setLocalConfig(config.loyaltyPointsBonusConfigV1);
      } else if (config === null) {
        // Reset to empty when config is null
        setLocalConfig([]);
      } else {
        console.warn('LoyaltyPointsBonusConfig - config exists but no loyaltyPointsBonusConfigV1:', config);
      }
    }
  }, [config]);
  // Notify parent when local config changes
  useEffect(() => {
    if (localConfig.length > 0) {
      const updatedConfig = {
        loyaltyPointsBonusConfigV1: localConfig,
      };
      onConfigChange(updatedConfig);
      if (onChange) {
        onChange(updatedConfig);
      }
    } else {
      // If localConfig is empty, we still need to notify parent with null/empty config
      onConfigChange(null);
      if (onChange) {
        onChange(null);
      }
    }
  }, [localConfig, onConfigChange, onChange]);
  if (isPromoMetaLoading) {
    return <Spinner />;
  }

  // Show info icon when loyalty bonus apply method is not set or is 'none'
  const shouldShowInfoIcon =
    localConfig.length === 0 &&
    (loyaltyBonusApplyMethod === 'none' || loyaltyBonusApplyMethod === '' || loyaltyBonusApplyMethod === null);

  if (shouldShowInfoIcon) {
    return (
      <Tooltip
        title={`Set the bonus apply method on the promo_code apply Loyalty Points Bonus (currently ${
          loyaltyBonusApplyMethod || 'not set'
        })`}
      >
        <InfoOutlinedIcon fontSize="small" />
      </Tooltip>
    );
  }

  const handleAddNew = () => {
    setEditingItem(null);
    reset({
      loyaltyPromoKey: '',
      loyaltyProductType: '',
      applyOrder: 0,
    });
    setIsEditModalOpen(true);
  };

  const handleEdit = (item: EditingItem) => {
    setEditingItem(item);
    reset({
      loyaltyPromoKey: item.loyaltyPromoKey,
      loyaltyProductType: item.loyaltyProductType,
      applyOrder: item.applyOrder,
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (id: string) => {
    setLocalConfig((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSave = (data: FormData) => {
    if (editingItem) {
      // Update existing item
      setLocalConfig((prev) =>
        prev.map((item) =>
          item.id === editingItem.id
            ? {
                ...item,
                loyaltyPromoKey: data.loyaltyPromoKey,
                loyaltyProductType: data.loyaltyProductType,
                applyOrder: data.applyOrder,
              }
            : item,
        ),
      );
    } else {
      // Add new item
      const newItem = {
        id: generateUniqueId(),
        loyaltyPromoKey: data.loyaltyPromoKey,
        loyaltyProductType: data.loyaltyProductType,
        applyOrder: data.applyOrder,
      };
      setLocalConfig((prev) => [...prev, newItem]);
    }
    setIsEditModalOpen(false);
  };

  const handleCancel = () => {
    setIsEditModalOpen(false);
    setEditingItem(null);
  };

  const sharedOpts: Partial<GridColDef> = {
    editable: false,
    sortable: false,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    flex: 1,
  };

  const columns: Array<GridColDef> = [
    {
      field: 'loyaltyPromoKey',
      headerName: 'Loyalty Promo Key',
      renderCell: (params) => params.value,
      ...sharedOpts,
      flex: 3,
    },
    {
      field: 'loyaltyProductType',
      headerName: 'Product Type',
      renderCell: (params) => getProductTypeLabel(params.value, promoLoyaltyMetaConfig),
      ...sharedOpts,
    },
    {
      field: 'applyOrder',
      headerName: 'Apply Order',
      renderCell: (params) => (params.value ? params.value.toString() : ''),
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      flex: 1,
      ...sharedOpts,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      renderCell: (params) => [
        <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={() => handleEdit(params.row)} color="primary" />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDelete(params.row.id)}
          color="error"
        />,
      ],
      ...sharedOpts,
    },
  ];

  type LoyaltyBonusApplyDescriptionProps = {
    loyaltyBonusApplyMethod: PromoLoyaltyMetaConfig['loyaltyBonusFilterMethods'][number]['key'];
    showNoneError: boolean;
  };

  const LoyaltyBonusApplyDescription: React.FC<LoyaltyBonusApplyDescriptionProps> = ({
    loyaltyBonusApplyMethod,
    showNoneError,
  }: LoyaltyBonusApplyDescriptionProps) => {
    if (!loyaltyBonusApplyMethod || loyaltyBonusApplyMethod === '') {
      return (
        <Alert severity="info">
          Loyalty Bonus Apply Method is not set. Please select a method to configure loyalty points.
        </Alert>
      );
    }

    const config = promoLoyaltyMetaConfig?.loyaltyBonusFilterMethods.find(
      (method) => method.key === loyaltyBonusApplyMethod,
    );
    if (!config) {
      return (
        <Alert severity="warning">
          No config found for loyalty bonus apply method '{loyaltyBonusApplyMethod}' (from{' '}
          {JSON.stringify(
            promoLoyaltyMetaConfig?.loyaltyBonusFilterMethods.map((method) => method.key),
            null,
            2,
          )}
          )
        </Alert>
      );
    }

    if (showNoneError && loyaltyBonusApplyMethod === 'none') {
      return (
        <Alert severity="warning">
          Loyalty Bonus Apply Method is set to 'None' on the Promo, which means no loyalty bonus points will be applied.
        </Alert>
      );
    }
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textDecoration: config.isDeprecated ? 'line-through' : 'none' }}
      >
        [{config.key}] - {config?.description}
      </Typography>
    );
  };

  const title = localConfig?.length > 0 ? `Loyalty Points (${localConfig?.length})` : 'Loyalty Points';
  const showAdvance =
    config?.loyaltyPointsBonusConfigV1?.some(
      (item) => item?.applyOrder || item?.loyaltyProductType || item?.loyaltyPromoKey,
    ) ?? false;

  return (
    <>
      <Accordion
        key={`loyalty-accordion-${localConfig?.length ?? 0}`}
        title={title}
        defaultExpanded={localConfig?.length > 0 ? true : false}
        columns={1}
      >
        <Box sx={{ width: '100%' }}>
          <Stack>
            <LoyaltyBonusApplyDescription
              key={`loyalty-description-${loyaltyBonusApplyMethod || 'none'}`}
              loyaltyBonusApplyMethod={loyaltyBonusApplyMethod}
              showNoneError={true}
            />
          </Stack>
        </Box>
        <DataGrid
          rows={localConfig}
          columns={columns}
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          hideFooter
          autoHeight
          initialState={{
            columns: {
              columnVisibilityModel: {
                loyaltyProductType: showAdvance,
                applyOrder: showAdvance,
              },
            },
          }}
        />
        <Button startIcon={<AddIcon />} onClick={handleAddNew} variant="contained" size="small">
          Add New
        </Button>
      </Accordion>
      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Loyalty Points Bonus' : 'Add New Loyalty Points Bonus'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <Controller
                  name="loyaltyPromoKey"
                  control={control}
                  rules={{ required: 'Loyalty Promo Key is required' }}
                  render={({ field }) => (
                    <LoyaltyPromoKeyOptionsSelector
                      loyaltyPromoKeyOptions={loyaltyPromoKeyOptions}
                      isPromoLoyaltyKeyOptionsLoading={isLoyaltyPromoKeyOptionsLoading}
                      value={field.value}
                      onChange={field.onChange}
                      isErroring={!!errors.loyaltyPromoKey}
                    />
                  )}
                />
              </Box>
              <Button
                onClick={() => window.open('/marketing/loyalty-promo-keys', '_blank')}
                variant="outlined"
                size="small"
              >
                Manage
              </Button>
            </Stack>

            <Accordion title="Advanced" defaultExpanded={showAdvance} columns={1}>
              <Controller
                name="loyaltyProductType"
                control={control}
                render={({ field }) => (
                  <LoyaltyPromoProductTypeOptionsSelector
                    promoLoyaltyMetaConfig={promoLoyaltyMetaConfig}
                    isPromoMetaLoading={isPromoMetaLoading}
                    value={field.value}
                    onChange={field.onChange}
                    isErroring={!!errors.loyaltyProductType}
                  />
                )}
              />
              <Controller
                name="applyOrder"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Apply Order (optional)"
                    type="number"
                    fullWidth
                    error={!!errors.applyOrder}
                    helperText={
                      'When an item qualitfies for multiple loyalty reward options, the one with the highest Apply Order will be applied.' +
                      (errors.applyOrder?.message ? ` ${errors.applyOrder?.message}` : '')
                    }
                    inputProps={{ min: 0 }}
                  />
                )}
              />
            </Accordion>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSubmit(handleSave)} variant="contained">
            {editingItem ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoyaltyPointsBonusConfig;
